<script setup lang="ts">
import type { ContentBookReader } from '~/models/Content/ContentBookReader'
import type { BaseItem } from '~/models/Content/BaseItem'
import type { ImageRelationField } from '~/models/Content/BaseField'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useImage from '~/composables/useImage'
import useDescriptor from '~/composables/useDescriptor'
import { useContentHelper } from '~/composables/useContentHelper'
import LinkOrAnchor from '~/components/utils/LinkOrAnchor.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import CardMenu from '~/components/cards/buttons/CardMenu.vue'

const props = defineProps<{ item: BaseItem | ContentBookReader }>()

const { t } = useI18n()
const { buildResourceUrl } = useUrlBuilder()
const { getImageAltText } = useImage()
const { isBookReader } = useContentHelper()
const { getDescriptor } = useDescriptor()

const imageLoaded = ref(false)

const link = computed(() => buildResourceUrl(props.item))
const label = computed(() => isBookReader(props.item) ? t('filters.product') : getDescriptor(props.item))
const imageAltText = computed(() => {
  if (!isBookReader(props.item)) {
    return ''
  }
  return Number(props.item.image?.destinationContentId) > 0
    ? getImageAltText(props.item.image as ImageRelationField)
    : (props.item.thumbnailAlt || '')
})
</script>

<template>
  <li class="border-purple-10 hover:bg-purple-5 relative row-span-2 grid grid-rows-subgrid border-t p-4 transition">
    <aside
      v-if="item.new"
      class="bg-green-40 pointer-events-none absolute top-0 right-0 rounded-bl-2xl p-1.5 pl-2 text-sm font-medium tracking-wider text-white uppercase"
      v-text="t('newShort')"
    />
    <div class="animate-fade order-1 flex size-full flex-col gap-2 self-end">
      <span class="font-semibold text-purple-50">
        <LinkOrAnchor
          class="line-clamp-3 outline-0 before:absolute before:inset-0 before:transition before:focus-visible:ring"
          :link="link"
        >
          {{ item.title }}
        </LinkOrAnchor>
      </span>
      <div class="order-first mt-2">
        <span
          class="text-coral-40 text-sm font-medium uppercase"
          v-text="label"
        />
      </div>
      <CardMenu
        :resource="item"
        toggle-color="purple"
        toggle-shade="dark"
        button-classes="z-10 mt-auto"
      />
    </div>
    <div class="flex w-full place-self-end overflow-hidden">
      <img
        v-if="item.thumbnail"
        v-show="imageLoaded"
        :src="item.thumbnail"
        :alt="imageAltText"
        class="animate-fade mt-auto size-full object-contain"
        @load="imageLoaded = true"
        @error="imageLoaded = true"
      >
      <AuSkeleton
        v-if="item.thumbnail && !imageLoaded"
        height="260px"
        background="var(--color-purple-10)"
        class="!rounded-none"
      />
    </div>
  </li>
</template>
